import { Button, Form, Input, Radio, Select } from 'antd';
import apiBanksCall from 'apps/investcore/apiCalls/bank/list';
import apiBankBranchCall from 'apps/investcore/apiCalls/data/bankBranches';
import { useFetchApiList } from 'hooks/api';
import { stockAccountStatusOptions } from 'apps/investment/constants/stock-accounts';
import {
  genderOptions,
  securityAccountCardTypeOptions,
} from 'apps/investment/constants/user';
import DatePicker from 'common/ui/UIDatePicker';
import { useEffect } from 'react';
import { formItemLayout, tailFormItemLayout } from 'utils/formConfig';
import { convertDateFormData, ensureDateFields } from 'utils/formData';
import { createVietnameseFilter } from 'utils/vietnamese-search';

const { Option } = Select;

const dateFields = ['date_of_birth', 'id_issue_date'];

const ItemForm = ({ form, submiting, onFinish, initialValues, name }) => {
  const {
    id,
    bank_branch_id,
    bank_name,
    bank_branch_name,
    account_number,
    contract_number,
  } = initialValues || {};

  const { bank_id } = (Form.useWatch([], form) as Record<string, any>) || {};

  useEffect(() => {
    if (!!initialValues) {
      form.setFieldsValue(ensureDateFields(initialValues, dateFields));
    }
  }, [form, initialValues]);

  const { data: banks = [], load: loadBanks } = useFetchApiList(apiBanksCall, { resourceName: 'data' });

  const { data: bankBranches = [], load: loadBankBranch } =
    useFetchApiList(apiBankBranchCall, { resourceName: 'data' });

  useEffect(() => {
    loadBanks({ page_size: 99 });
  }, []);

  useEffect(() => {
    if (bank_id) {
      loadBankBranch({ id: bank_id, page_size: 99 });
    }
  }, [bank_id]);

  return (
    <Form
      {...formItemLayout}
      form={form}
      name={name || 'user_form'}
      onFinish={values => {
        if (!id) {
          values.status = 1002;
        }

        onFinish({ ...convertDateFormData(values, dateFields) });
      }}
      scrollToFirstError
    >
      <Form.Item name="status" label="Trạng thái">
        <Select>
          {stockAccountStatusOptions.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item name="source" label="Nguồn">
        <Select>
          {[
            { value: null, label: 'Chưa có' },
            { value: 'stag', label: 'Stag' },
            { value: 'nhsv', label: 'nhsv' },
          ].map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="id_type" label="Loại giấy tờ định danh">
        <Select>
          {securityAccountCardTypeOptions &&
            securityAccountCardTypeOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
        </Select>
      </Form.Item>

      <Form.Item name="id_number" label="Số định danh cá nhân">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="email" label="Email">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="phone" label="Phone">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="full_name" label="Full Name">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="date_of_birth" label="Ngày tháng năm sinh">
        <DatePicker />
      </Form.Item>

      <Form.Item name="gender" label="Giới tính">
        <Select>
          {genderOptions &&
            genderOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
        </Select>
      </Form.Item>

      <Form.Item name="id_issue_date" label="Ngày cấp">
        <DatePicker />
      </Form.Item>

      <Form.Item name="id_issue_place" label="Nơi cấp">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="residential_address" label="Địa chỉ thường trú">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="address" label="Địa chỉ liên hệ">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="bank_id" label="Ngân hàng">
        <Select
          showSearch
          optionFilterProp="name"
          options={banks}
          fieldNames={{ label: 'name', value: 'id' }}
          placeholder="Select Bank"
          filterOption={createVietnameseFilter(['name', 'code'])}
        />
      </Form.Item>

      <Form.Item name="bank_branch_id" label="Chi Nhánh Ngân hàng">
        <Select
          placeholder="Select a option and change input text above"
          allowClear
          defaultValue={{
            value: bank_branch_id,
            label: bank_branch_name,
          }}
        >
          {bankBranches?.map(({ id, name }) => (
            <Option value={id}>{name}</Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="bank_account_number" label="STK Ngân hàng">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="bank_account_name" label="Tên tài khoản ngân hàng">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="tax_number" label="Mã số thuế">
        <Input placeholder="" />
      </Form.Item>

      <Form.Item name="broker_name" label="Broker Name">
        <Input placeholder="e.g. stagvn" />
      </Form.Item>

      <Form.Item
        name="authentication_method"
        label="Phương thức xác thực đăng nhập"
      >
        <Radio.Group>
          <Radio value={'otp'}>OTP</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="enable_payment_in_advance"
        label="Ứng trước tiền bán chứng khoán"
      >
        <Radio.Group>
          <Radio value={true}>Có</Radio>
          <Radio value={false}>Không</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="enable_margin_trading_account"
        label="Mở tài khoản giao dịch ký quỹ (margin)"
      >
        <Radio.Group>
          <Radio value={true}>Có</Radio>
          <Radio value={false}>Không</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="is_account_mapping" label="Payment Area Mapping">
        <Radio.Group>
          <Radio value={true}>Có</Radio>
          <Radio value={false}>Không</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="nationality" label="Quốc tịch">
        <Radio.Group>
          <Radio value={'Việt Nam'}>Việt Nam</Radio>
          <Radio value={'Khác'}>Khác</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item {...tailFormItemLayout}>
        <Button type="primary" htmlType="submit" loading={submiting}>
          Save
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ItemForm;
